import type { ReactNode } from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface Props {
  /** Field label (required — a11y). */
  label: ReactNode;
  /** HTML id — wires label ↔ control and, when present, error's aria-describedby. */
  htmlFor?: string;
  /** Helper copy shown below the label, before the control. */
  description?: ReactNode;
  /** Validation error message. When present, the field renders in an error state. */
  error?: string;
  /** Marks the label with an "(optional)" hint. Default: false (i.e. field is required). */
  optional?: boolean;
  /** The control itself — Input / Textarea / Select / Checkbox / etc. */
  children: ReactNode;
  className?: string;
}

// Non-RHF label + control + description + error composite. Use this for
// local-state forms (e.g. the small dialogs on CustomerDetail — slug edit,
// email edit, reset password) where wiring react-hook-form would be
// overkill. RHF forms use the shadcn `Form* /FormField/FormItem/…`
// primitives from `components/ui/form.tsx` directly (see IssueForm and
// CustomerCreate for reference implementations).
//
// The historical name was `FormField`, but that collided with the RHF
// controller of the same name exported by shadcn's Form primitive.
// Renamed to `LabelledField` in Chunk 5B Step 0 to settle the pattern.
export function LabelledField({
  label,
  htmlFor,
  description,
  error,
  optional,
  children,
  className,
}: Props) {
  const errorId = htmlFor ? `${htmlFor}-error` : undefined;
  return (
    <div className={cn('space-y-1.5', className)} data-testid="form-field">
      {htmlFor ? (
        <Label htmlFor={htmlFor} className="flex items-center gap-1">
          {label}
          {optional && <span className="text-xs text-muted-foreground">(optional)</span>}
        </Label>
      ) : (
        <Label className="flex items-center gap-1">
          {label}
          {optional && <span className="text-xs text-muted-foreground">(optional)</span>}
        </Label>
      )}
      {description && (
        <p className="text-xs text-muted-foreground" data-testid="form-field-description">
          {description}
        </p>
      )}
      {children}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="text-xs text-destructive"
          data-testid="form-field-error"
        >
          {error}
        </p>
      )}
    </div>
  );
}
